import {Box, Typography, useTheme} from '@mui/material'
import {
  area,
  curveLinear,
  curveMonotoneX,
  curveStepAfter,
  line,
  scaleLinear,
  scalePoint,
  scaleTime,
} from 'd3'
import type {FC, ReactElement} from 'react'
import {memo, useMemo, useRef, useState} from 'react'
import {getFormattedDate} from '@app/util-functions'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import type {UserInterface} from '@interfaces/user'
import {isActualDateString} from '@shared/utils/dateUtils'
import {nivoDarkColors} from '@shared/components/graphThemes/nivoDarkColors'
import {nivoLightColors} from '@shared/components/graphThemes/nivoLightColors'
import ChartTooltip from './ChartTooltip'
import ChartActionsMenu from './ChartActionsMenu'
import {CHART_VIEW_H as VIEW_H, CHART_VIEW_W as VIEW_W, sanitizeChartColor} from './chartConstants'
import {resizableGraphPanelSx} from './chartStyles'
import {useResizeGrip} from '@containers/Dashboards/AgentChat/components/messages/useResizeGrip'
import type {LineChartSpec} from './types'

/**
 * Generic line chart for the chat. Native d3 for the maths, React renders the SVG,
 * MUI theme drives the colours. Time axes are localised via getFormattedDate. Hover
 * shows a slice guide + per-series values. Supports `area` (fill), `stacked` (stacked
 * area, shared x), and `curve` ('monotone' | 'linear' | 'step' for state signals).
 */

const MARGIN = {top: 12, right: 18, bottom: 34, left: 52}
const INNER_W = VIEW_W - MARGIN.left - MARGIN.right
const INNER_H = VIEW_H - MARGIN.top - MARGIN.bottom
const HOUR = 3_600_000

type XMode = 'linear' | 'time' | 'category'

interface Props {
  spec: LineChartSpec
}

interface Row {
  name: string
  color: string
  y: number
  py: number
}
interface Slice {
  px: number
  label: string
  rows: Row[]
}

const ChatLineChart: FC<Props> = ({spec}): ReactElement | null => {
  const theme = useTheme()
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  const palette = theme.palette.mode === 'dark' ? nivoDarkColors : nivoLightColors
  const axisColor = theme.palette.text.secondary
  const gridColor = theme.palette.divider

  const panelRef = useRef<HTMLDivElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hover, setHover] = useState<{idx: number; left: number; top: number} | null>(null)
  useResizeGrip(panelRef, {mode: 'aspect', aspectRatio: VIEW_W / VIEW_H})

  const stacked = !!spec.stacked

  const model = useMemo(() => {
    const allX = spec.series.flatMap((s) => s.points.map((p) => p.x))
    const allNumeric = allX.every((x) => typeof x === 'number' && Number.isFinite(x))
    // isActualDateString (not raw Date.parse) so a bare-year/numeric label like "2026"
    // stays a category instead of flipping the axis to time mode.
    const allDate = !allNumeric && allX.every((x) => typeof x === 'string' && isActualDateString(x))
    const mode: XMode = allNumeric ? 'linear' : allDate ? 'time' : 'category'

    const categories: string[] = []
    if (mode === 'category') {
      for (const s of spec.series) {
        for (const p of s.points) {
          const label = String(p.x)
          if (!categories.includes(label)) categories.push(label)
        }
      }
    }

    const toXValue = (x: number | string): number | Date | string => {
      if (mode === 'linear') return Number(x)
      if (mode === 'time') return new Date(String(x))
      return String(x)
    }

    const dates = mode === 'time' ? allX.map((x) => new Date(String(x)).getTime()) : []
    const spanMs = dates.length ? Math.max(...dates) - Math.min(...dates) : 0
    const fmtTime = (d: Date, detailed: boolean): string => {
      const opts = detailed
        ? {showDate: true, showTime: true}
        : spanMs <= 36 * HOUR
          ? {showDate: false, showTime: true}
          : {showDate: true, showTime: false, showYear: spanMs > 300 * 24 * HOUR}
      return getFormattedDate(d, currentUser as UserInterface | null, opts)
    }

    let xScale: (v: number | Date | string) => number
    let xTicks: {pos: number; label: string}[]

    if (mode === 'category') {
      const scale = scalePoint<string>().domain(categories).range([0, INNER_W]).padding(0.5)
      xScale = (v) => scale(String(v)) ?? 0
      const step = Math.ceil(categories.length / 7)
      xTicks = categories
        .filter((_, i) => i % step === 0)
        .map((label) => ({pos: scale(label) ?? 0, label}))
    } else if (mode === 'time') {
      const scale = scaleTime()
        .domain([new Date(Math.min(...dates)), new Date(Math.max(...dates))])
        .range([0, INNER_W])
      xScale = (v) => scale(v as Date)
      xTicks = scale.ticks(5).map((t) => ({pos: scale(t), label: fmtTime(t, false)}))
    } else {
      const nums = allX.map((x) => Number(x))
      const scale = scaleLinear()
        .domain([Math.min(...nums), Math.max(...nums)])
        .range([0, INNER_W])
        .nice()
      const fmt = scale.tickFormat(5)
      xScale = (v) => scale(v as number)
      xTicks = scale.ticks(5).map((t) => ({pos: scale(t), label: fmt(t)}))
    }
    const X = (xRaw: number | string): number => xScale(toXValue(xRaw))

    const curve =
      spec.curve === 'step'
        ? curveStepAfter
        : spec.curve === 'linear'
          ? curveLinear
          : curveMonotoneX
    const filled = stacked || !!spec.area

    const keyOf = (x: number | string): string =>
      mode === 'time' ? String(new Date(String(x)).getTime()) : String(x)
    const yAt = (s: {points: {x: number | string; y: number}[]}, key: string): number =>
      s.points.find((p) => keyOf(p.x) === key)?.y ?? 0

    // Ordered unique x (needed for stacking + aligned slices).
    const xOrder: {key: string; xRaw: number | string}[] = []
    const seenX = new Set<string>()
    spec.series.forEach((s) =>
      s.points.forEach((p) => {
        const k = keyOf(p.x)
        if (!seenX.has(k)) {
          seenX.add(k)
          xOrder.push({key: k, xRaw: p.x})
        }
      }),
    )
    if (mode !== 'category') {
      const num = (x: number | string): number => {
        const v = toXValue(x)
        return v instanceof Date ? v.getTime() : (v as number)
      }
      xOrder.sort((a, b) => num(a.xRaw) - num(b.xRaw))
    }

    const allY = spec.series.flatMap((s) => s.points.map((p) => p.y))
    const stackTotals = xOrder.map(({key}) =>
      spec.series.reduce((sum, s) => sum + Math.max(0, yAt(s, key)), 0),
    )
    const yScale = scaleLinear()
      .domain(stacked ? [0, Math.max(0, ...stackTotals)] : [Math.min(...allY), Math.max(...allY)])
      .range([INNER_H, 0])
      .nice()
    const yFmt = yScale.tickFormat(5)
    const yTicks = yScale.ticks(5).map((t) => ({pos: yScale(t), label: yFmt(t)}))

    let paths: {name: string; color: string; d: string}[]
    let areas: {name: string; color: string; d: string}[] = []
    let slices: Slice[]

    if (stacked) {
      const cum: Record<string, number> = {}
      const built = spec.series.map((s, i) => {
        const color = sanitizeChartColor(s.color) ?? palette[i % palette.length]
        const pts = xOrder.map(({key, xRaw}) => {
          const y = Math.max(0, yAt(s, key))
          const y0 = cum[key] ?? 0
          const y1 = y0 + y
          cum[key] = y1
          return {xRaw, key, y, y0, y1}
        })
        return {name: s.name, color, pts}
      })
      const areaGen = area<{xRaw: number | string; y0: number; y1: number}>()
        .x((p) => X(p.xRaw))
        .y0((p) => yScale(p.y0))
        .y1((p) => yScale(p.y1))
        .curve(curve)
      const topLine = line<{xRaw: number | string; y1: number}>()
        .x((p) => X(p.xRaw))
        .y((p) => yScale(p.y1))
        .curve(curve)
      areas = built.map((b) => ({name: b.name, color: b.color, d: areaGen(b.pts) ?? ''}))
      paths = built.map((b) => ({name: b.name, color: b.color, d: topLine(b.pts) ?? ''}))
      slices = xOrder.map(({key, xRaw}) => {
        const label = mode === 'time' ? fmtTime(toXValue(xRaw) as Date, true) : String(xRaw)
        const rows = built.map((b) => {
          const pt = b.pts.find((p) => p.key === key)
          return {name: b.name, color: b.color, y: pt?.y ?? 0, py: yScale(pt?.y1 ?? 0)}
        })
        return {px: X(xRaw), label, rows}
      })
    } else {
      const lineGen = line<{x: number | string; y: number}>()
        .x((p) => X(p.x))
        .y((p) => yScale(p.y))
        .curve(curve)
      paths = spec.series.map((s, i) => ({
        name: s.name,
        color: sanitizeChartColor(s.color) ?? palette[i % palette.length],
        d: lineGen(s.points) ?? '',
      }))
      if (filled) {
        const areaGen = area<{x: number | string; y: number}>()
          .x((p) => X(p.x))
          .y0(INNER_H)
          .y1((p) => yScale(p.y))
          .curve(curve)
        areas = spec.series.map((s, i) => ({
          name: s.name,
          color: sanitizeChartColor(s.color) ?? palette[i % palette.length],
          d: areaGen(s.points) ?? '',
        }))
      }
      const sliceMap = new Map<string, Slice>()
      spec.series.forEach((s, i) => {
        const color = sanitizeChartColor(s.color) ?? palette[i % palette.length]
        for (const p of s.points) {
          const key = keyOf(p.x)
          if (!sliceMap.has(key)) {
            const label = mode === 'time' ? fmtTime(toXValue(p.x) as Date, true) : String(p.x)
            sliceMap.set(key, {px: X(p.x), label, rows: []})
          }
          sliceMap.get(key)?.rows.push({name: s.name, color, y: p.y, py: yScale(p.y)})
        }
      })
      slices = [...sliceMap.values()].sort((a, b) => a.px - b.px)
    }

    return {paths, areas, xTicks, yTicks, slices}
  }, [spec, palette, currentUser, stacked])

  const onMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const el = wrapRef.current
    if (!el || !model.slices.length) return
    const rect = el.getBoundingClientRect()
    const scaleX = rect.width / VIEW_W
    const innerX = (e.clientX - rect.left) / scaleX - MARGIN.left
    let best = 0
    let bestD = Infinity
    model.slices.forEach((s, i) => {
      const d = Math.abs(s.px - innerX)
      if (d < bestD) {
        bestD = d
        best = i
      }
    })
    const left = (MARGIN.left + model.slices[best].px) * scaleX
    setHover({idx: best, left, top: e.clientY - rect.top})
  }

  if (!model.paths.length) return null

  const multi = spec.series.length > 1
  const active = hover ? model.slices[hover.idx] : null

  return (
    <Box ref={panelRef} sx={resizableGraphPanelSx}>
      <ChartActionsMenu
        svgRef={svgRef}
        title={spec.title}
        legend={multi ? model.paths.map((p) => ({label: p.name, color: p.color})) : undefined}
      />
      {spec.title ? (
        <Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 600}}>
          {spec.title}
        </Typography>
      ) : null}

      {multi ? (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5}}>
          {model.paths.map((p) => (
            <Box key={p.name} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Box sx={{width: 10, height: 10, borderRadius: '2px', bgcolor: p.color}} />
              <Typography variant="caption" sx={{color: 'text.secondary'}}>
                {p.name}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        ref={wrapRef}
        data-graph-area
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${VIEW_W} / ${VIEW_H}`,
          overflow: 'visible',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={spec.title ?? 'line chart'}
          style={{width: '100%', height: '100%', display: 'block', overflow: 'visible'}}
        >
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {model.yTicks.map((t) => (
              <g key={`y-${t.label}`} transform={`translate(0,${t.pos})`}>
                <line x1={0} x2={INNER_W} stroke={gridColor} strokeWidth={1} opacity={0.5} />
                <text x={-8} dy="0.32em" textAnchor="end" fontSize={11} fill={axisColor}>
                  {t.label}
                </text>
              </g>
            ))}

            <line
              y1={INNER_H}
              y2={INNER_H}
              x1={0}
              x2={INNER_W}
              stroke={axisColor}
              strokeWidth={1}
            />
            {model.xTicks.map((t) => (
              <text
                key={`x-${t.label}-${t.pos}`}
                x={t.pos}
                y={INNER_H + 18}
                textAnchor="middle"
                fontSize={11}
                fill={axisColor}
              >
                {t.label}
              </text>
            ))}

            {model.areas.map((a) => (
              <path
                key={`area-${a.name}`}
                d={a.d}
                fill={a.color}
                fillOpacity={stacked ? 0.85 : 0.18}
                stroke="none"
              />
            ))}

            {model.paths.map((p) => (
              <path key={p.name} d={p.d} fill="none" stroke={p.color} strokeWidth={2} />
            ))}

            {/* hover slice guide + markers (drawn last, on top) */}
            {active ? (
              <g>
                <line
                  x1={active.px}
                  x2={active.px}
                  y1={0}
                  y2={INNER_H}
                  stroke={axisColor}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  opacity={0.6}
                />
                {active.rows.map((r) => (
                  <circle key={r.name} cx={active.px} cy={r.py} r={3.5} fill={r.color} />
                ))}
              </g>
            ) : null}
          </g>
        </svg>

        <ChartTooltip left={hover?.left ?? 0} top={hover?.top ?? 0} visible={Boolean(active)}>
          {active ? (
            <>
              <Typography variant="caption" sx={{fontWeight: 600, display: 'block'}}>
                {active.label}
              </Typography>
              {active.rows.map((r) => (
                <Box key={r.name} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Box sx={{width: 8, height: 8, borderRadius: '2px', bgcolor: r.color}} />
                  <Typography variant="caption" sx={{color: 'text.secondary'}}>
                    {multi ? `${r.name}: ` : ''}
                    {r.y}
                  </Typography>
                </Box>
              ))}
            </>
          ) : null}
        </ChartTooltip>
      </Box>
    </Box>
  )
}

// Memoised: during streaming the parent re-renders on every token, but the spec
// identity is stable (memoised by raw in ChartBlock), so this fully skips re-render.
export default memo(ChatLineChart)
